import type { BoxCollider, FlatFloor, FloorRegion } from './collision';
import { PLAYER_RADIUS } from './controller';

/**
 * B-2 "Enterable interiors" (PLAN-CITY2 §3) — the second walk-through room in
 * the block, built the SAME way `world/interior.ts`'s bodega was (isolated
 * origin, teleport-only reachable, its own solid gray-box walls with a real
 * doorway gap) rather than through a shared abstraction. The bodega's own
 * pattern took three review rounds to get right (T3-1 + two follow-ups,
 * STATUS-CITY.md's 2026-08-26 entries) — copying its EXACT reviewed formulas
 * verbatim into a second file is lower-risk than retrofitting both rooms
 * through a new shared engine, and keeps this room's own math fully
 * self-contained and auditable on its own, matching how every other
 * near-identical structure in this project (each boss, each enemy) is its
 * own hand-authored file rather than a generic config.
 *
 * `LAUNDROMAT_ORIGIN` (x=250) sits 110m from `INTERIOR_ORIGIN` (x=140,
 * interior.ts) — comfortably past BOTH rooms' own `>50`-from-origin
 * self-healing-invariant thresholds (main.ts), so a stray flag desync could
 * never misclassify a player standing in one room as "still near" the other.
 * Also comfortably outside `NavGrid`'s BOUNDS (x -30..30), every
 * `ai/habitats.ts` zone, and the S05 boundary ring (x -38..38) — the same
 * isolation property interior.ts's own header comment establishes for the
 * bodega.
 */
export const LAUNDROMAT_ORIGIN = { x: 250, z: 0 };

const HALF_WIDTH = 3; // X
const HALF_DEPTH = 2.5; // Z
export const LAUNDROMAT_ROOM = {
  minX: LAUNDROMAT_ORIGIN.x - HALF_WIDTH,
  maxX: LAUNDROMAT_ORIGIN.x + HALF_WIDTH,
  minZ: LAUNDROMAT_ORIGIN.z - HALF_DEPTH,
  maxZ: LAUNDROMAT_ORIGIN.z + HALF_DEPTH,
  height: 2.6, // matches interior.ts's INTERIOR_ROOM — a touch above PLAYER_TOTAL_HEIGHT (1.4), same "cozy interior ceiling" call
};

/**
 * Same OrbitCamera-vs-small-room math as `INTERIOR_CAMERA_MAX_DISTANCE`
 * (interior.ts's own comment has the full derivation) — at pitch 0.55rad,
 * a `d` pull-back puts the camera `1.0 + d*sin(0.55)` up and `d*cos(0.55)`
 * out horizontally. At d=2.6: ~2.36m up (clears this room's 2.6m ceiling by
 * 0.24m, identical margin to the bodega) and ~2.22m out (clears this room's
 * SMALLER 2.5m half-depth by 0.28m — tighter than the bodega's 3.0m half-
 * depth margin, but still real and positive, confirmed by this exact
 * arithmetic rather than assumed to transfer unchanged from a differently-
 * sized room).
 */
export const LAUNDROMAT_CAMERA_MAX_DISTANCE = 2.6;

/** Where the player lands after walking/pressing through the exterior door — just inside the near (south) wall, facing into the room, mirroring INTERIOR_SPAWN's exact offset convention. */
export const LAUNDROMAT_SPAWN = { x: LAUNDROMAT_ORIGIN.x, z: LAUNDROMAT_ROOM.maxZ - 1.5 };

/** The EXIT interactable — close to LAUNDROMAT_SPAWN, same "door and register both near the entrance" reasoning as interior.ts's own INTERIOR_EXIT_MARKER. */
export const LAUNDROMAT_EXIT_MARKER = { x: LAUNDROMAT_ORIGIN.x, z: LAUNDROMAT_ROOM.maxZ - 0.6 };

/**
 * A bank of coin-op washers along the back wall — gray-box dressing (§0.4),
 * height 0.9 (below controller.ts's VAULT_MAX_HEIGHT 1.1, matching
 * INTERIOR_COUNTER's exact proven-safe height band) so it's a real vault
 * target rather than either a wall or an invisible walk-through prop —
 * `laundromatMachinesTopFloor()` below is what makes vaulting onto it not
 * oscillate (same PARK_PROP_TOPS/interiorCounterTopFloor() requirement).
 * Pushed flush against the back (north) wall (cz - depth/2 exactly equals
 * the wall's own inner face, LAUNDROMAT_ROOM.minZ + 0.4) — a deliberate
 * flush placement, not a gap, matching how real laundromat machines sit
 * against a wall.
 */
export const LAUNDROMAT_MACHINES = {
  x: LAUNDROMAT_ORIGIN.x,
  z: LAUNDROMAT_ROOM.minZ + 0.4 + 0.4, // wall inner face + half-depth (0.8/2)
  width: 3.6,
  depth: 0.8,
  height: 0.9,
  color: 0xaeb7c4,
};

/** The "reason to enter" — one-time discovery reward, same pattern as world/interact.ts's `hidden_stash` (S10). Standing point south of the machine bank, clear of it by 0.6m real margin. */
export const LAUNDROMAT_STASH_MARKER = { x: LAUNDROMAT_ORIGIN.x, z: LAUNDROMAT_MACHINES.z + LAUNDROMAT_MACHINES.depth / 2 + 0.6 };

export const LAUNDROMAT_FLOOR: FloorRegion = {
  kind: 'flat',
  minX: LAUNDROMAT_ROOM.minX,
  maxX: LAUNDROMAT_ROOM.maxX,
  minZ: LAUNDROMAT_ROOM.minZ,
  maxZ: LAUNDROMAT_ROOM.maxZ,
  y: 0,
  priority: 1, // same "own isolated XZ footprint, no descending-region tie to break" reasoning as INTERIOR_FLOOR
};

/** interiorWalls()'s own south-doorway gap half-width, mirrored here — hoisted so the stoop below always matches the gap it catches exactly (Classic gotcha #19's formula-drift lesson, same discipline interior.ts already follows). */
const DOOR_HALF_WIDTH = 1.1;

/** Four solid walls with a real south-face threshold gap — same segment idiom as `interiorWalls()` (interior.ts) and `buildingWalls()` (layout.ts). */
export function laundromatWalls(): BoxCollider[] {
  const t = 0.4;
  const r = LAUNDROMAT_ROOM;
  const doorCenterX = LAUNDROMAT_ORIGIN.x;
  return [
    { minX: r.minX, maxX: r.maxX, minZ: r.minZ, maxZ: r.minZ + t, topY: r.height, bottomY: 0 }, // north (back)
    { minX: r.minX, maxX: doorCenterX - DOOR_HALF_WIDTH, minZ: r.maxZ - t, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // south, west of the door gap
    { minX: doorCenterX + DOOR_HALF_WIDTH, maxX: r.maxX, minZ: r.maxZ - t, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // south, east of the door gap
    { minX: r.minX, maxX: r.minX + t, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // west
    { minX: r.maxX - t, maxX: r.maxX, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // east
  ];
}

/**
 * The doorway gap's own walkable patch, south of the room — same "a catch,
 * not a wall" role `INTERIOR_STOOP_FLOOR` plays for the bodega (its own
 * comment has the full "why a patch instead of nothing" story: without SOME
 * floor here, simply walking toward the door before the auto-exit trigger
 * fires steps off into open air past `LAUNDROMAT_ROOM.maxZ`, since
 * `LAUNDROMAT_ORIGIN` is fully isolated with no floor beyond this room).
 */
export const LAUNDROMAT_STOOP_FLOOR: FlatFloor = {
  kind: 'flat',
  minX: LAUNDROMAT_ORIGIN.x - DOOR_HALF_WIDTH - 0.2,
  maxX: LAUNDROMAT_ORIGIN.x + DOOR_HALF_WIDTH + 0.2,
  minZ: LAUNDROMAT_ROOM.maxZ,
  maxZ: LAUNDROMAT_ROOM.maxZ + 1.5,
  y: 0,
  priority: 1,
};

/** Closes the stoop's own open south edge — same `interiorStoopCurb()` idiom (interior.ts), applied here from day one rather than needing its own review round to discover the gap the bodega's FIRST cut shipped without. */
export function laundromatStoopCurb(): BoxCollider {
  return {
    minX: LAUNDROMAT_STOOP_FLOOR.minX,
    maxX: LAUNDROMAT_STOOP_FLOOR.maxX,
    minZ: LAUNDROMAT_STOOP_FLOOR.maxZ,
    maxZ: LAUNDROMAT_STOOP_FLOOR.maxZ + 0.4,
    topY: 1.6,
    bottomY: 0,
  };
}

/**
 * Crossing this threshold auto-runs the exit routine — the T3-1 follow-up's
 * proven fix, applied here from the start instead of shipping the bodega's
 * OWN first (stoop-only, no auto-exit) cut and rediscovering the same two
 * real-play gaps a second time. Same derivation as
 * `isPastDoorwayThreshold` (interior.ts): the x-band is the stoop's own
 * span plus the real capsule radius plus a small margin, so the whole
 * doorway gap (including its corners) is covered.
 */
const DOORWAY_BAND_MARGIN = PLAYER_RADIUS + 0.05;
export function isPastLaundromatDoorwayThreshold(x: number, z: number): boolean {
  return (
    z > LAUNDROMAT_ROOM.maxZ &&
    x > LAUNDROMAT_STOOP_FLOOR.minX - DOORWAY_BAND_MARGIN &&
    x < LAUNDROMAT_STOOP_FLOOR.maxX + DOORWAY_BAND_MARGIN
  );
}

/** Solid obstacle — see `LAUNDROMAT_MACHINES`'s own comment for why 0.9m makes this a vault target, not a wall. */
export function laundromatMachinesCollider(): BoxCollider {
  const m = LAUNDROMAT_MACHINES;
  return {
    minX: m.x - m.width / 2,
    maxX: m.x + m.width / 2,
    minZ: m.z - m.depth / 2,
    maxZ: m.z + m.depth / 2,
    topY: m.height,
    bottomY: 0,
  };
}

/** The machine bank's own top surface as a real floor region — without this, vaulting onto it free-falls straight back through and re-triggers the vault every frame (layout.ts's `PARK_PROP_TOPS` comment has the full mechanism). */
export function laundromatMachinesTopFloor(): FloorRegion {
  const m = LAUNDROMAT_MACHINES;
  return {
    kind: 'flat',
    minX: m.x - m.width / 2,
    maxX: m.x + m.width / 2,
    minZ: m.z - m.depth / 2,
    maxZ: m.z + m.depth / 2,
    y: m.height,
  };
}
